import {
  Row,
  Col,
  Card,
  Table,
  Button,
  Input,
  Tag,
  DatePicker,
  Tooltip,
  Avatar,
  Image,
} from "antd";
import React, { useState, useContext, useEffect } from "react";
import useRequest from "../../../hooks/useRequest";
import * as XLSX from "xlsx";
import { ShowToast, Severty } from "../../../helper/toast";
import useDebounce from "../../../hooks/useDebounce";
import moment from "moment";
import { useNavigate } from "react-router";
import ShowTotal from "../../../components/ShowTotal";
import apiPath from "../../../constants/apiPath";
import UserFrom from "../UserFrom";
import ImportForm from "../../../components/ImportForm";
import ConfirmationBox from "../../../components/ConfirmationBox";
import { DownloadExcel } from "../../../components/ExcelFile";
import { AuthContext } from "../../../context/AuthContext";
import lang from "../../../helper/lang";
import { USERTYPE } from "../../../constants/conts";
import { UndoOutlined } from "@ant-design/icons";
import FilterBar, { FilterBarFilters } from "../../../components/FilterBar";

const Search = Input.Search;
const { RangePicker } = DatePicker;

function Index() {
  const sectionName = lang("Customer");
  const routeName = "customer";

  const api = {
    status: apiPath.statusCustomer,
    delete: apiPath.deleteCustomer,
    addEdit: apiPath.addEditCustomer,
    list: apiPath.listCustomer,
    importFile: apiPath.importCustomer,
  };

  const [searchText, setSearchText] = useState("");
  const { userProfile, userPermission } = useContext(AuthContext);
  const { request } = useRequest();
  const { showConfirm } = ConfirmationBox();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const [visible, setVisible] = useState(false);
  const [importVisible, setImportVisible] = useState(false);
  const [selected, setSelected] = useState();
  const [startDate, setStartDate] = useState();
  const [endDate, setEndDate] = useState();
  const [filter, setFilter] = useState({
    type: undefined,
  });

  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const [filters, setFilters] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const debouncedSearchText = useDebounce(searchText, 300);
  const navigate = useNavigate();

  const isAddPermissionGranted =
    userPermission?.CustomerSection?.includes("add");
  const addPermissionTooltip = isAddPermissionGranted
    ? ""
    : "Permission Not Granted";
  const addDisabledAttribute = isAddPermissionGranted ? "" : "disabled";

  const isUpdatePermissionGranted =
    userPermission?.CustomerSection?.includes("edit");
  const updateDisabledAttribute = isUpdatePermissionGranted ? "" : "disabled";
  const updatePermissionTooltip = isUpdatePermissionGranted
    ? `${lang("Update")} ` + sectionName
    : "Permission Not Granted";
  const updateAttributeColor = isUpdatePermissionGranted ? "purple" : "red";

  const isViewPermissionGranted =
    userPermission?.CustomerSection?.includes("view");
  const viewDisabledAttribute = isViewPermissionGranted ? "" : "disabled";
  const viewPermissionTooltip = isViewPermissionGranted
    ? `${lang("View")} ` + sectionName
    : "Permission Not Granted";
  const viewAttributeColor = isViewPermissionGranted ? "purple" : "red";

  const view = (id) => {
    navigate(`/user/${routeName}/view/${id}`);
  };

  const activity = (id) => {
    navigate(`/user/activity/${id}`);
  };

  const columns = [
    {
      title: `${lang("Name")}`,
      dataIndex: "name",
      key: "name",
      width: 200,
      render: (_, { name, _id, image }) => {
        const capitalize = (str) =>
          str ? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase() : "Not Available";

        const capitalizedName = capitalize(name);
        return (
          <>
            {!image ? (
              <>
                <Avatar className="cap" size={40}>
                  {name ? name.charAt(0) : "Not Available"}
                </Avatar>
                <a className="cap avatar-text" onClick={(e) => view(_id)}>
                  {capitalizedName}
                </a>
              </>
            ) : (
              <>
                <Image className="image-index-radius" src={image} />
                <a className="cap avatar-text" onClick={(e) => view(_id)}>
                  {capitalizedName}
                </a>
              </>
            )}
          </>
        );
      },
      sorter: (a, b) => {
        let nameA = (a.name || "").toLowerCase();
        let nameB = (b.name || "").toLowerCase();
        return nameA.localeCompare(nameB);
      },
    },
    {
      title: `${lang("Email")} ${lang("Address")}`,
      dataIndex: "email",
      key: "email",
      width: 210,
      render: (_, { email }) => {
        return email ? email : "-";
      },
      sorter: (a, b) => {
        let nameA = a.email;
        let nameB = b.email;
        if (nameA < nameB) return -1;
        if (nameA > nameB) return 1;
        return 0;
      },
    },
    {
      title: `${lang("Mobile")} ${lang("Number")}`,
      render: (_, { mobile_number, country_code }) => {
        return (
          (country_code ? "+" + country_code + "-" : "-") +
          (mobile_number ? mobile_number : "")
        );
      },
      sorter: (a, b) => {
        let nameA = a.mobile_number;
        let nameB = b.mobile_number;
        if (nameA < nameB) return -1;
        if (nameA > nameB) return 1;
        return 0;
      },
    },
    {
      title: `${lang("Account Type")}`,
      dataIndex: "isPrivate",
      key: "isPrivate",
      width: 210,
      render: (_, { isPrivate }) => {
        return isPrivate ? lang("Private") : lang("Public");
      },
      sorter: (a, b) => a.isPrivate - b.isPrivate,
    },
    {
      title: `${lang("Status")}`,
      key: "is_active",
      filters: [
        {
          text: "Active",
          value: true,
        },
        {
          text: "Inactive",
          value: false,
        },
      ],
      render: (_, { is_delete, is_active, _id }) => {
        let color = is_active ? "green" : "red";
        const type = "is_active";
        return !is_delete ? (
          <a>
            <Tag
              onClick={(e) =>
                showConfirm({
                  record: _id,
                  path: api.status,
                  onLoading: () => setLoading(true),
                  onSuccess: () => setRefresh((prev) => !prev),
                  type,
                })
              }
              color={color}
              key={is_active}
            >
              {is_active ? "Active" : "Inactive"}
            </Tag>
          </a>
        ) : (
          <Tag color={"red"} key={_id}>
            {" "}
            Account Deleted{" "}
          </Tag>
        );
      },
    },
    {
      title: `${lang("Registered_On")}`,
      key: "created_at",
      dataIndex: "created_at",
      render: (_, { created_at }) => {
        return moment(created_at).format("DD-MM-YYYY hh:mm:ss A");
      },
      sorter: (a, b) => {
        let nameA = a.created_at;
        let nameB = b.created_at;
        if (nameA < nameB) return -1;
        if (nameA > nameB) return 1;
        return 0;
      },
    },
    {
      title: `Joined Contest`,
      key: "totalContest",
      dataIndex: "totalContest",
    },
    {
      title: `${lang("Action")}`,

      render: (_, record) => {
        return (
          <>
            {userProfile && userProfile?.user_type === USERTYPE.SUB_ADMIN
              ? !record.is_delete && (
                <Tooltip
                  title={updatePermissionTooltip}
                  color={updateAttributeColor}
                  key={"update" + routeName}
                >
                  <Button
                    disabled={updateDisabledAttribute}
                    onClick={() => {
                      setSelected(record);
                      setVisible(true);
                    }}
                  >
                    {" "}
                    <i className="fa fa-light fa-pen"></i>{" "}
                  </Button>
                </Tooltip>
              )
              : !record.is_delete && (
                <Tooltip
                  title={`${lang("Update")} ` + sectionName}
                  color={"purple"}
                  key={"update" + routeName}
                >
                  <Button
                    onClick={() => {
                      setSelected(record);
                      setVisible(true);
                    }}
                  >
                    {" "}
                    <i className="fa fa-light fa-pen"></i>{" "}
                  </Button>
                </Tooltip>
              )}

            {userProfile && userProfile?.user_type === USERTYPE.SUB_ADMIN ? (
              <Tooltip
                title={viewPermissionTooltip}
                color={viewAttributeColor}
                key={"view" + routeName}
              >
                <Button
                  disabled={viewDisabledAttribute}
                  onClick={(e) => view(record._id)}
                >
                  {" "}
                  <i className="fa fa-light fa-eye"></i>{" "}
                </Button>
              </Tooltip>
            ) : (
              <Tooltip
                title={`${lang("View")} ` + sectionName}
                color={"purple"}
                key={"view" + routeName}
              >
                <Button onClick={(e) => view(record._id)}>
                  {" "}
                  <i className="fa fa-light fa-eye"></i>{" "}
                </Button>
              </Tooltip>
            )}
          </>
        );
      },
    },
  ];

  useEffect(() => {
    setLoading(true);
    fetchData(pagination, filters);
  }, [refresh, debouncedSearchText, startDate, endDate]);

  const fetchData = (pagination, filters) => {
    const filterActive = filters ? filters.is_active : null;
    const filterVerify = filters ? filters.is_verify : null;
    setFilters({ is_active: filterActive, is_verify: filterVerify });
    request({
      url:
        api.list +
        `?verify=${filterVerify ? filterVerify.join(",") : ""}&status=${filterActive ? filterActive.join(",") : ""
        }&page=${pagination ? pagination.current : 1}&limit=${pagination ? pagination.pageSize : 10
        }&search=${debouncedSearchText}&start_date=${startDate ? startDate : ""
        }&end_date=${endDate ? endDate : ""}`,
      method: "GET",
      onSuccess: (data) => {
        setLoading(false);
        setList(data.data.list.docs);
        setPagination({
          current: pagination.current,
          total: data.data.list.totalDocs,
          pageSize: Number(data.data.list.limit),
        });
      },
      onError: (error) => {
        console.log(error);
        setLoading(false);
        ShowToast(error, Severty.ERROR);
      },
    });
  };

  const handleChange = (pagination, filters) => {
    fetchData(pagination, filters);
  };

  const onSearch = (e) => {
    setSearchText(e.target.value);
    setPagination({ current: 1, pageSize: pagination.pageSize });
  };

  const resetData = () => {
    setSearchText("");
    setStartDate("");
    setEndDate("");
    setFilter({
      type: undefined,
    });
    setRefresh((prev) => !prev);
  };


  const handleChangeDate = (e) => {
    if (e != null) {
      setStartDate(moment(e[0]._d).format("YYYY-MM-DD"));
      setEndDate(moment(e[1]._d).format("YYYY-MM-DD"));
    } else {
      setStartDate();
      setEndDate();
    }
  };
  const handleExportClick = async () => {
    try {
      let list = [];
      request({
        url:
          api.list +
          `?page=1&limit=${pagination ? pagination.total : 10
          }&search=${debouncedSearchText}&start_date=${startDate ? startDate : ""
          }`,
        method: "GET",
        onSuccess: (res) => {
          setLoading(false);
          const list = res.data.list.docs;

          const data = list.map((row) => ({
            Name: row.name ? row.name[0].toUpperCase() + row.name.slice(1) : "-",
            Email: row.email ? row.email : "-",
            "Country Code": row.country_code ? "+" + row.country_code : "-",
            "Mobile Number": row.mobile_number ? row.mobile_number : "-",
            Status: row.is_active ? "Active" : "Inactive",
            Location: row.location ? row.location : "-",
            "Registered On": moment(row.created_at).format("DD-MM-YYYY"),
          }));

          const heading = [
            `Veegram Customer Report - ${moment().format("DD-MMM-YYYY")}`,
          ];

          const combinedData = [
            [heading],
            Object.keys(data[0]),
            ...data.map((row) => Object.values(row)),
          ];

          const workbook = XLSX.utils.book_new();
          const worksheet = XLSX.utils.aoa_to_sheet(combinedData);

          worksheet["!merges"] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 6 } }];

          // Set column widths
          const colWidths = [
            { wch: 10 },
            { wch: 15 },
            { wch: 20 },
            { wch: 20 },
            { wch: 22 },
            { wch: 15 },
            { wch: 15 },
            { wch: 15 },
            { wch: 15 },
          ];
          worksheet["!cols"] = colWidths;
          const fileName = `Veegram Customer ${moment().format("DD-MMM-YYYY")}`;
          XLSX.utils.book_append_sheet(workbook, worksheet, fileName);
          XLSX.writeFile(
            workbook,
            `${moment().milliseconds() + 1000 * (moment().seconds() + 60 * 60)
            } - Veegram Customer - ${moment().format("DD-MMM-YYYY")}.xlsx`
          );
        },
        onError: (error) => {
          console.log(error);
          ShowToast(error, Severty.ERROR);
        },
      });

    } catch (error) {
      console.log(error);
    }
  };

  return (
    <>
      <div className="tabled customerMain">
        <Row gutter={[24, 0]}>
          <Col xs="24" xl={24}>
            <Card
              bordered={false}
              className="criclebox tablespace mb-24"
              title={sectionName + ` ${lang("Manager")}`}
              extra={
                <FilterBar
                  showFilters={showFilters}
                  onToggle={() => setShowFilters((v) => !v)}
                  searchContent={
                    <Search
                      className="mainSearch"
                      size="large"
                      allowClear
                      onChange={onSearch}
                      value={searchText}
                      onPressEnter={onSearch}
                      placeholder={`${lang("Search")} ${lang("By")} ${lang(
                        "Customer Name"
                      )}, ${lang("Mobile")}, ${lang("Email")}`}
                    />
                  }
                  rightContent={
                    <div className="button_group">
                      {userProfile &&
                        userProfile?.user_type === USERTYPE.SUB_ADMIN ? (
                        <Tooltip
                          title={addPermissionTooltip}
                          color={"red"}
                          key={"view" + routeName}
                        >
                          <Button
                            onClick={(e) => {
                              setVisible(true);
                              setSearchText("");
                            }}
                            disabled={addDisabledAttribute}
                          >
                            {" "}
                            {lang("Add")} {sectionName}{" "}
                          </Button>
                        </Tooltip>
                      ) : (
                        <Button
                          onClick={(e) => {
                            setVisible(true);
                            setSearchText("");
                          }}
                        >
                          {" "}
                          {lang("Add")} {sectionName}{" "}
                        </Button>
                      )}

                      <Button
                        onClick={(e) => handleExportClick()}
                      >
                        <i className="fas fa-cloud-download-alt"></i>
                        &nbsp;&nbsp;
                        {lang("Export")}
                      </Button>
                    </div>
                  }
                />
              }
            >
              {showFilters && (
                <FilterBarFilters>
                  <RangePicker
                    disabledDate={(current) =>
                      current && current.isAfter(moment().endOf("day"))
                    }
                    onChange={handleChangeDate}
                    value={
                      startDate && endDate
                        ? [moment(startDate), moment(endDate)]
                        : null
                    }
                  />
                  <Button
                    onClick={() => resetData()}
                    type="primary"
                    icon={<UndoOutlined />}
                  >
                    Reset
                  </Button>
                </FilterBarFilters>
              )}
              <h4 className="text-right mb-1">
                {pagination.total ? ShowTotal(pagination.total) : ShowTotal(0)}
              </h4>
              <div className="table-responsive customPagination">
                <Table
                  loading={loading}
                  columns={columns}
                  dataSource={list}
                  pagination={{
                    current: pagination.current,
                    defaultPageSize: pagination.pageSize,
                    responsive: true,
                    total: pagination.total,
                    showSizeChanger: true,
                    pageSizeOptions: ["10", "20", "30", "50"],
                  }}
                  onChange={handleChange}
                  className="ant-border-space"
                />
              </div>
            </Card>
          </Col>
        </Row>
      </div>

      {visible && (
        <UserFrom
          type={sectionName}
          path={api.addEdit}
          sectionName={sectionName}
          show={visible}
          hide={() => {
            setSelected();
            setVisible(false);
          }}
          data={selected}
          refresh={() => setRefresh((prev) => !prev)}
        />
      )}

      {importVisible && (
        <ImportForm
          path={api.importFile}
          sectionName={sectionName}
          show={importVisible}
          hide={() => setImportVisible(false)}
          existingData={list}
          refresh={() => setRefresh((prev) => !prev)}
        />
      )}
    </>
  );
}
export default Index;
